import moment from 'moment';
import React, { useEffect, useRef } from 'react';
import { UiTrashAlt } from '@vyaguta/vyaguta-icons/ui';

import Tooltip from 'components/common/tooltip';
import DropDown from 'components/common/dropDown';
import InputWrapper from 'components/common/inputWrapper';
import DatePicker from 'components/common/datepicker/DatePicker';

import { isObjectEmpty } from 'utils/object';
import { getFormattedDate, getMonthsLaterDate } from 'utils/date';

import { ENGAGEMENT_STATUS_IDS, FIXED_TERM_CONTRACT } from 'constants/employeeStatus';
import { EMPLOYEE_STATUS_PROBATION_DETAILS_VALUES } from 'constants/employeeStatusDetails';

interface EmpStatusDetailItem {
  keyId: number;
  category: string;
  date?: string;
}

interface EmployeeStatusPayload {
  transitionDate: string;
  engagementStatus: {
    id: number;
    name: string;
  };
}

interface AddEmployeeStatusDetailsProps {
  item: EmpStatusDetailItem;
  setEmpStatusDetails: (updater: (prev: EmpStatusDetailItem[]) => EmpStatusDetailItem[]) => void;
  empStatusDetails: EmpStatusDetailItem[];
  handleBlur: (event: { target: { name: string } }) => void;
  handleDelete: () => void;
  error?: {
    category?: string | false;
    date?: string | false;
  };
  employeeStatusPayload: EmployeeStatusPayload;
  index: number;
  editIndex?: number;
  type: string;
}

const AddEmployeeStatusDetails: React.FC<AddEmployeeStatusDetailsProps> = ({
  item,
  setEmpStatusDetails,
  empStatusDetails,
  handleBlur,
  handleDelete,
  error,
  employeeStatusPayload,
  index,
  editIndex,
  type,
}) => {
  const getUpdatedData = (prev: EmpStatusDetailItem[], key: 'category' | 'date', value: string) => {
    const newData = prev.map((data) => (data.keyId === item.keyId ? { ...data, [key]: value } : data));

    return newData;
  };

  const onDropDownChange = (data: { value: string }) => {
    const { value } = data;
    setEmpStatusDetails((prev) => getUpdatedData(prev, 'category', value));
  };

  const onDateChange = (value: Date | string) => {
    setEmpStatusDetails((prev) => getUpdatedData(prev, 'date', getFormattedDate(value)));
  };

  const setEmpStatusDetailsRef = useRef(setEmpStatusDetails);
  useEffect(() => {
    setEmpStatusDetailsRef.current = setEmpStatusDetails;
  });

  const empStatusDetailsRef = useRef(empStatusDetails);
  useEffect(() => {
    empStatusDetailsRef.current = empStatusDetails;
  });

  useEffect(() => {
    if (typeof editIndex === 'number') {
      return;
    }

    if (employeeStatusPayload.engagementStatus.id === ENGAGEMENT_STATUS_IDS.FIXED_TERM_CONTRACT) {
      return;
    }

    const newData = empStatusDetailsRef.current?.map((empStatusDetailsItem, currentIndex) => {
      return {
        ...empStatusDetailsItem,
        date:
          (currentIndex < 2 && getMonthsLaterDate(employeeStatusPayload.transitionDate, 3 * (currentIndex + 1))) ||
          empStatusDetailsItem.date,
      };
    });

    setEmpStatusDetailsRef.current(() => newData);
  }, [editIndex, employeeStatusPayload.engagementStatus.id, employeeStatusPayload.transitionDate]);

  const getDefaultValue = (statusItem: EmpStatusDetailItem) => {
    if (!statusItem.category || isObjectEmpty({ category: statusItem.category })) {
      return null;
    }

    return {
      label: statusItem.category,
      value: statusItem.category,
    };
  };

  return (
    <div className="emp-status-details-form__category">
      <div className="d-flex">
        <InputWrapper label="Category" className="emp-status-details-form__input" isMandatory error={error?.category}>
          <DropDown
            hasError={Boolean(error?.category)}
            defaultValue={getDefaultValue(item)}
            onDropDownChange={onDropDownChange}
            options={EMPLOYEE_STATUS_PROBATION_DETAILS_VALUES}
            isSearchable={true}
            isDisabled={type === FIXED_TERM_CONTRACT}
            onBlur={() => handleBlur({ target: { name: `empStatusHistory.empStatusDetails[${index}].category` } })}
            customClassName="employee-form__dropdown"
            placeholder={'Select...'}
          />
        </InputWrapper>

        <InputWrapper
          label="Date"
          className="employee-form__date emp-status-details-form__input"
          isMandatory
          error={error?.date}
        >
          <DatePicker
            hasError={Boolean(error?.date)}
            date={item.date ? moment(item.date) : null}
            onDateChange={onDateChange}
            isOutsideRange={(date) => date.isBefore(employeeStatusPayload.transitionDate)}
            onClose={() => handleBlur({ target: { name: `empStatusHistory.empStatusDetails[${index}].date` } })}
          />
        </InputWrapper>

        <div className="emp-status-details-form__delete" onClick={handleDelete}>
          <Tooltip
            html={
              <div className="tooltip-info">
                <p className="tooltip-info__title">Delete</p>
              </div>
            }
          >
            <div className="icon-container">
              <UiTrashAlt />
            </div>
          </Tooltip>
        </div>
      </div>
      <div className="emp-status-details-form__divider"></div>
    </div>
  );
};

export default AddEmployeeStatusDetails;
